import type { PostHog } from 'posthog-js/dist/module.full.no-external';

export const POSTHOG_RETRY_DELAY_MS = 60 * 1000;

let initialization: Promise<PostHog | undefined> | undefined;
let retryAt = 0;
let recordingVisitId: string | null = null;

function posthogKey(): string {
  if (
    import.meta.env.MODE === 'test' ||
    import.meta.env.VITE_SENTRY_ENVIRONMENT === 'ci'
  ) {
    return '';
  }
  return String(import.meta.env.VITE_POSTHOG_KEY ?? '').trim();
}

export function isPostHogEnabled(): boolean {
  return posthogKey() !== '';
}

export function initPostHog(): Promise<PostHog | undefined> {
  const key = posthogKey();
  if (!key || (!initialization && Date.now() < retryAt)) {
    return Promise.resolve(undefined);
  }

  initialization ??= import('posthog-js/dist/module.full.no-external')
    .then(({ default: posthog }) =>
      posthog.init(key, {
        api_host:
          import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
        persistence: 'memory',
        disable_persistence: true,
        person_profiles: 'never',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_dead_clicks: false,
        capture_heatmaps: false,
        capture_performance: false,
        capture_exceptions: false,
        rageclick: false,
        disable_session_recording: true,
        enable_recording_console_log: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '*',
          blockSelector:
            'img, video, canvas, svg, iframe, .sentry-block, .sentry-mask, input[type="hidden"], input[type="file"]',
          maskAttributeFn: (name, value) => {
            if (name === 'class') return value;
            if (name === 'style') return /url\s*\(/i.test(value) ? '' : value;
            if (
              (name === 'width' || name === 'height') &&
              /^\d+(\.\d+)?$/.test(value)
            )
              return value;
            return '***';
          },
          recordHeaders: false,
          recordBody: false,
          captureCanvas: { recordCanvas: false },
          maskCapturedNetworkRequestFn: () => null,
        },
        disable_surveys: true,
        disable_web_experiments: true,
        disable_external_dependency_loading: true,
        advanced_disable_flags: false,
        advanced_disable_feature_flags: true,
        save_campaign_params: false,
        save_referrer: false,
        property_denylist: [
          '$current_url',
          '$pathname',
          '$referrer',
          '$referring_domain',
          '$initial_current_url',
          '$initial_pathname',
          '$initial_referrer',
          '$initial_referring_domain',
          '$session_entry_url',
          '$session_entry_pathname',
          '$session_entry_referrer',
          '$prev_pageview_url',
          '$prev_pageview_pathname',
        ],
      })
    )
    .catch(() => {
      // A temporary chunk load failure must not silence a kiosk until reload.
      initialization = undefined;
      retryAt = Date.now() + POSTHOG_RETRY_DELAY_MS;
      console.warn('[PostHog] Initialization failed');
      return undefined;
    });
  return initialization;
}

function recordingAction(action: () => void): void {
  try {
    action();
  } catch {
    console.warn('[PostHog] Session recording unavailable');
  }
}

export function captureKioskAnalytics(
  event: string,
  properties: Record<string, unknown>,
  timestamp: Date,
  visitId: string | null
): void {
  void initPostHog()
    .then((client) => {
      if (!client) return;
      if (visitId && recordingVisitId !== visitId) {
        recordingVisitId = visitId;
        recordingAction(() => {
          client.stopSessionRecording();
          client.reset(true);
          client.register({
            visit_id: visitId,
            app: 'b2c-kiosk',
            kiosk_slug: properties.kiosk_slug,
          });
          client.startSessionRecording();
        });
      }
      try {
        client.capture(event, properties, {
          timestamp,
          ...(event === 'visit_ended' || event === 'visit_interrupted'
            ? { send_instantly: true, transport: 'sendBeacon' as const }
            : {}),
        });
      } finally {
        if (
          visitId &&
          event === 'visit_ended' &&
          recordingVisitId === visitId
        ) {
          recordingVisitId = null;
          recordingAction(() => {
            client.stopSessionRecording();
            client.reset(true);
          });
        }
      }
    })
    .catch(() => undefined);
}

export function captureUpsell(
  event: string,
  sessionId: string,
  properties: Record<string, unknown> = {}
): void {
  if (!sessionId) {
    return;
  }
  const snapshot = structuredClone(properties);
  void initPostHog()
    .then((client) => {
      client?.capture(event, {
        ...snapshot,
        app: 'customer-web',
        environment: analyticsEnvironment(),
        schema_version: 1,
        business_session_id: sessionId,
        distinct_id: `greenscreen:${sessionId}`,
        $device_id: `greenscreen:${sessionId}`,
        $session_id: sessionId,
        $process_person_profile: false,
        // Sent from the guest's own phone: its IP must not become their location.
        $geoip_disable: true,
      });
    })
    .catch(() => undefined);
}

export function analyticsEnvironment(): string {
  const hostname = window.location.hostname;
  if (
    ['localhost', '127.0.0.1'].includes(hostname) ||
    hostname.endsWith('.local')
  ) {
    return 'development';
  }
  if (/staging|stage|dev\./.test(hostname)) {
    return 'staging';
  }
  return 'production';
}
